/**
 * Utilitaires assurance emprunteur — codes Annexe K Cofidis
 *
 * Règles métier basées sur l'âge de l'emprunteur (produit B 21508 / E14) :
 *  - 18 à < 65 ans : DC PTIA /IT /PE ou DC PTIA /IT
 *  - 65 à < 75 ans : aucune assurance (Cofidis rejette DC seul et DC PTIA seul)
 *  - ≥ 75 ans      : aucune assurance
 */

export const ALL_INSURANCE_CODES = [
  'DC PTIA /IT /PE',
  'DC PTIA /IT',
  'DC',
] as const;

export type InsuranceCode = typeof ALL_INSURANCE_CODES[number] | 'N';

export interface InsuranceOption {
  value: InsuranceCode;
  description: string;
}

export const INSURANCE_DESCRIPTIONS: Record<InsuranceCode, string> = {
  'DC PTIA /IT /PE': "Couverture maximale incluant la garantie perte d'emploi",
  'DC PTIA /IT':     "Couverture décès, PTIA et incapacité temporaire de travail",
  'DC':              "Couverture décès uniquement",
  'N':               "Je ne souhaite pas souscrire à une assurance",
};

export function calculateAge(birthDate: string | undefined): number {
  if (!birthDate) return 0;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

/**
 * Renvoie les codes assurance autorisés selon l'âge de l'emprunteur.
 */
export function getAvailableInsuranceCodes(birthDate: string | undefined): InsuranceCode[] {
  const age = calculateAge(birthDate);
  if (age < 65) return ['DC PTIA /IT /PE', 'DC PTIA /IT', 'N'];
  if (age < 75) return ['DC', 'N']; // 65–74 ans : décès uniquement
  return ['N']; // ≥ 75 ans : aucune assurance
}

export function getInsuranceOptions(birthDate: string | undefined): InsuranceOption[] {
  return getAvailableInsuranceCodes(birthDate).map(value => ({
    value,
    description: INSURANCE_DESCRIPTIONS[value],
  }));
}

/**
 * Renvoie le code assurance par défaut selon l'âge de l'emprunteur.
 */
export function getDefaultInsuranceCode(birthDate: string | undefined): InsuranceCode {
  const age = calculateAge(birthDate);
  if (age < 65) return 'DC PTIA /IT /PE';
  if (age < 75) return 'DC';
  return 'N';
}

/**
 * Vérifie si un code assurance est compatible avec l'âge de l'emprunteur.
 * Renvoie le code tel quel s'il est valide, sinon le meilleur code disponible.
 * Renvoie null si aucune assurance ne doit être envoyée à l'API.
 */
export function sanitizeInsuranceCode(
  code: string | null | undefined,
  birthDate: string | undefined
): string | null {
  if (code === null || code === undefined || code === 'N') return null;
  const available = getAvailableInsuranceCodes(birthDate).filter(c => c !== 'N');
  if ((available as string[]).includes(code)) return code;
  const fallback = getDefaultInsuranceCode(birthDate);
  return fallback === 'N' ? null : (fallback as string);
}
